import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { Empty } from "./Primitives";

const tooltipStyle = { borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 };

export const ClaimsPerDayChart = ({ volume }) => {
  const daily = volume.daily;
  const total = daily.reduce((s, d) => s + d.count, 0);
  if (!total) return <Empty />;
  const tickEvery = Math.max(1, Math.ceil(daily.length / 12));
  return (
    <div>
      <div className="h-56" data-testid="claims-per-day-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={daily} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} interval={tickEvery - 1} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#eff6ff" }} formatter={(v) => [v, "Claims"]} />
            <Bar dataKey="count" fill="#2563eb" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-4">
        <Totals title="Weekly totals" rows={volume.weekly.slice(-8).map((w) => ({ label: `Wk of ${w.week_start.slice(5).replace("-", "/")}`, count: w.count }))} testId="weekly-totals" />
        <Totals title="Monthly totals" rows={volume.monthly.slice(-6).map((m) => ({ label: m.label, count: m.count }))} testId="monthly-totals" />
      </div>
    </div>
  );
};

const Totals = ({ title, rows, testId }) => (
  <div data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">{title}</p>
    <ul className="divide-y divide-gray-100 text-sm">
      {rows.map((r) => (
        <li key={r.label} className="flex justify-between py-1">
          <span className="text-gray-600">{r.label}</span>
          <span className="font-semibold tabular-nums">{r.count}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const UpcomingChart = ({ upcoming: raw }) => {
  const upcoming = raw.map((d) => ({ ...d, short: d.label.split(" ")[1] }));
  const total = upcoming.reduce((s, d) => s + d.count, 0);
  return (
    <div className="h-48" data-testid="upcoming-chart">
      {total === 0 ? (
        <Empty text="No installs scheduled in the next 14 days" />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={upcoming} margin={{ top: 16, right: 4, left: -24, bottom: 0 }}>
            <XAxis dataKey="short" tick={{ fontSize: 10, fill: "#6b7280" }} interval={1} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#ecfdf5" }} formatter={(v) => [v, "Installs"]} labelFormatter={(_, p) => p?.[0]?.payload?.label || ""} />
            <Bar dataKey="count" radius={[3, 3, 0, 0]}>
              <LabelList dataKey="count" position="top" style={{ fontSize: 11, fill: "#374151" }} formatter={(v) => (v ? v : "")} />
              {upcoming.map((d, i) => <Cell key={d.date} fill={i === 0 ? "#059669" : "#34d399"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export const Funnel = ({ funnel }) => {
  const max = Math.max(...funnel.stages.map((s) => s.count), funnel.denied, 1);
  const total = funnel.stages.reduce((s, x) => s + x.count, 0) + funnel.denied;
  if (!total) return <Empty />;
  return (
    <div data-testid="status-funnel">
      <ol className="space-y-2">
        {funnel.stages.map((s) => (
          <li key={s.status} className="flex items-center gap-3 text-sm">
            <span className="w-36 shrink-0 text-gray-700">{s.status}</span>
            <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
              <div className="h-full bg-blue-500 rounded flex items-center justify-end pr-2 text-white text-xs font-semibold" style={{ width: `${Math.max((s.count / max) * 100, s.count ? 8 : 0)}%` }}>
                {s.count || ""}
              </div>
            </div>
            {!s.count && <span className="text-xs text-gray-400 w-6">0</span>}
          </li>
        ))}
        <li className="flex items-center gap-3 text-sm pt-2 border-t border-dashed border-gray-200">
          <span className="w-36 shrink-0 text-gray-700">Denied</span>
          <div className="flex-1 h-6 bg-gray-100 rounded overflow-hidden">
            <div className="h-full bg-red-400 rounded flex items-center justify-end pr-2 text-white text-xs font-semibold" style={{ width: `${Math.max((funnel.denied / max) * 100, funnel.denied ? 8 : 0)}%` }}>
              {funnel.denied || ""}
            </div>
          </div>
          {!funnel.denied && <span className="text-xs text-gray-400 w-6">0</span>}
        </li>
      </ol>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="rounded-md bg-emerald-50 p-3">
          <p className="text-xs text-emerald-700 font-semibold uppercase">Approved</p>
          <p className="text-2xl font-bold text-emerald-700" data-testid="approval-rate">{funnel.approval_rate}%</p>
        </div>
        <div className="rounded-md bg-red-50 p-3">
          <p className="text-xs text-red-700 font-semibold uppercase">Denied</p>
          <p className="text-2xl font-bold text-red-700" data-testid="denial-rate">{funnel.denial_rate}%</p>
        </div>
      </div>
      {funnel.top_denial_reasons.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Top denial reasons</p>
          <ul className="space-y-1.5 text-sm">
            {funnel.top_denial_reasons.map((r) => (
              <li key={r.reason} className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-red-50 text-red-700 text-xs font-bold flex items-center justify-center">{r.count}</span>
                <span className="text-gray-700 line-clamp-2">{r.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
